import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Download, CheckCircle, XCircle } from 'lucide-react';
import { accountService } from '../api/accountRepo';
import { BulkImportData } from '../schemas/account';
import { canManageStaffDB } from '../../../lib/rbac';
import { useStore } from '../../../lib/store';
import { exportToCSV } from '../../../lib/csv';
import toast from 'react-hot-toast';

const AccountImport: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useStore();
  const [csvData, setCsvData] = useState<string>('');
  const [preview, setPreview] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [commitFunction, setCommitFunction] = useState<(() => Promise<any>) | null>(null);
  const [canManageAccounts, setCanManageAccounts] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  useEffect(() => {
    checkPermissions();
  }, [user]);

  const checkPermissions = async () => {
    if (!user?.id) {
      setCanManageAccounts(false);
      setCheckingPermissions(false);
      return;
    }

    try {
      const canManage = await canManageStaffDB(user.id);
      setCanManageAccounts(canManage);
    } catch (error) {
      console.error('Error checking permissions:', error);
      setCanManageAccounts(false);
    } finally {
      setCheckingPermissions(false);
    }
  };

  if (checkingPermissions) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!canManageAccounts) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500">Access denied. Admin privileges required.</div>
      </div>
    );
  }

  const downloadTemplate = () => {
    const templateData = [
      { empCode: 'STF-0001', email: 'doctor@medsystem.com', role: 'doctor', phone: '+1234567890' },
      { empCode: 'STF-0002', email: 'nurse@medsystem.com', role: 'nurse', phone: '+1234567891' },
    ];
    
    exportToCSV(
      templateData,
      ['Employee Code', 'Email', 'Role', 'Phone (Optional)'],
      (item) => [item.empCode, item.email, item.role, item.phone],
      'account-import-template.csv'
    );
  };

  const parseCsv = (csvText: string): BulkImportData[] => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) return [];
    
    lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    const data: BulkImportData[] = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
      if (values.length >= 3) {
        data.push({
          empCode: values[0],
          email: values[1],
          role: values[2] as any,
          phone: values[3] || undefined,
        });
      }
    }
    
    return data;
  };

  const handlePreview = async () => {
    if (!csvData.trim()) {
      toast.error('Please enter CSV data');
      return;
    }
    
    try {
      setLoading(true);
      const parsedData = parseCsv(csvData);
      
      if (parsedData.length === 0) {
        toast.error('No valid data found in CSV');
        return;
      }
      
      const result = await accountService.bulkImport(parsedData);
      setPreview(result.preview);
      setCommitFunction(() => result.commit || null);
      
      const validCount = result.preview.filter(p => p.status === 'valid').length;
      const errorCount = result.preview.filter(p => p.status === 'error').length;
      
      toast.success(`Preview generated: ${validCount} valid, ${errorCount} errors`);
    } catch (error) {
      toast.error('Failed to process CSV data');
      console.error('Error processing CSV:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCommit = async () => {
    if (!commitFunction) return;
    
    try {
      setLoading(true);
      const accounts = await commitFunction();
      toast.success(`Successfully created ${accounts.length} accounts`);
      navigate('/staff/accounts');
    } catch (error) {
      toast.error('Failed to create accounts');
      console.error('Error creating accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const validCount = preview.filter(p => p.status === 'valid').length;
  const errorCount = preview.filter(p => p.status === 'error').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/staff/accounts')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bulk Import Accounts</h1>
          <p className="text-gray-600">Import multiple staff accounts from CSV</p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-800 mb-2">CSV Format Instructions</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Column 1: Employee Code (must match existing staff)</li>
          <li>• Column 2: Email Address (must be unique)</li>
          <li>• Column 3: Role (doctor, nurse, pharmacist, etc.)</li>
          <li>• Column 4: Phone Number (optional)</li>
        </ul>
        <button
          onClick={downloadTemplate}
          className="mt-3 inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
        >
          <Download className="w-4 h-4 mr-1" />
          Download Template
        </button>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-medium text-gray-900 mb-4">CSV Data</h3>
        <textarea
          value={csvData}
          onChange={(e) => setCsvData(e.target.value)}
          placeholder="Paste your CSV data here or upload a file..."
          className="w-full h-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
        />
        
        <div className="mt-4 flex space-x-3">
          <button
            onClick={handlePreview}
            disabled={loading || !csvData.trim()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
          >
            <Upload className="w-4 h-4" />
            <span>{loading ? 'Processing...' : 'Preview Import'}</span>
          </button>
          
          {commitFunction && (
            <button
              onClick={handleCommit}
              disabled={loading || validCount === 0}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
            >
              <CheckCircle className="w-4 h-4" />
              <span>{loading ? 'Creating...' : `Create ${validCount} Accounts`}</span>
            </button>
          )}
        </div>
      </div>

      {preview.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Import Preview</h3>
            <p className="text-sm text-gray-600">
              {validCount} valid accounts, {errorCount} errors
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Error
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {preview.map((row, index) => (
                  <tr key={index} className={row.status === 'error' ? 'bg-red-50' : 'bg-green-50'}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {row.status === 'valid' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {row.empCode}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {row.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {row.role}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                      {row.error || ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountImport;
